import React, { createContext, useCallback, useContext, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useGoogleLogin, googleLogout } from "@react-oauth/google";
import axios from "axios";
import Cookies from "js-cookie";
import { startAgentSession } from "./agentSession";
import {
  ACC_SESSION_EXPIRED_EVENT,
  clearAccSession,
  ensureAccSession,
  resetAccSessionExpiry,
  startAccSession,
} from "./accSession";
import { useLocation, useNavigate } from "react-router-dom";
import { clearAgentSessionToken, clearGoogleAccessToken, setGoogleAccessToken } from "./googleAccessToken";
import {
  KIOSK_SIGNED_OUT_EVENT,
  KIOSK_SIGNED_OUT_NOTICE,
  clearKioskFlag,
  isKioskSession,
  setKioskNotice,
} from "./kioskFlag";
import { endKioskSession, isKioskUser, startKioskSession } from "./kioskSession";
import { resetUserCaches } from "./userCaches";

const AuthContext = createContext(null);

// One source of truth for how long a signed-in session lasts. The cookie expiry and the
// auto-logout timer used to carry their own literals (1/6 of a day and 4 * 60 * 60 * 1000),
// which is the same number written two ways - change one and the app either logs you out
// while the cookie is still valid or keeps a dead session on screen.
//
// Note this is deliberately longer than the Google access token behind it (~1 hour). The
// dashboard stays usable for the full session; the one page that calls a Google-authenticated
// API surfaces a "session expired / re-authenticate" prompt when the token lapses.
const SESSION_HOURS = 4;
const SESSION_MS = SESSION_HOURS * 60 * 60 * 1000;
const SESSION_DAYS = SESSION_HOURS / 24;
/** Long enough for an agent cold start to finish, short enough that the user is still here. */
const AGENT_SESSION_RETRY_MS = 4000;

const KIOSK_SIGN_IN_PATH = '/login';
const GOOGLE_SIGN_IN_PATH = '/login/google';
// `/kiosk` is an alias of the kiosk screen; none of the three is held behind the guard.
const isSignInPath = (path) => path === KIOSK_SIGN_IN_PATH || path === '/kiosk' || path === GOOGLE_SIGN_IN_PATH;

function AuthProvider({ children, onLoginSuccess }) {
  const [user, setUser] = useState(() => {
    const userData = Cookies.get('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A lapsed identity takes over the screen where the trainer already is, rather than bouncing
  // them to a sign-in screen: the route survives, so does the page's in-progress state, and one takeover
  // answers the whole burst of 401s that a shared iPad throws when the four-hour cookie dies
  // mid-shift. The ref is what the route guard and the login handler read - both run before a
  // state update would have landed.
  const [expired, setExpired] = useState(false);
  const signInRef = useRef(null);
  const expiredRef = useRef(false);
  // True when the takeover went up over a real page. A takeover raised on a sign-in screen is
  // holding nothing open, so signing in from it is an ordinary sign-in and belongs on the dashboard.
  const heldRouteRef = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Read by signOutLoud, which must not take `pathname` as a dependency of its own: it is in
  // the four-hour timer's dependency array, and a callback that changed identity per route
  // would restart that timer on every click and never log anybody out.
  const pathnameRef = useRef(pathname);
  const navigateRef = useRef(navigate);
  // The raw cookie already reflected in `user`. Re-parsing it hands `user` a new object
  // identity, and the effects keyed on it (the four-hour auto-logout, the ACC exchange) would
  // restart on every route change.
  const loadedUserCookie = useRef(Cookies.get('user') ?? null);

  useEffect(() => {
    pathnameRef.current = pathname;
    navigateRef.current = navigate;
  }, [pathname, navigate]);

  // The takeover can rise over an open dialog whose focus trap would otherwise keep Tab
  // inside it, out of reach of the one button that gets the person back in.
  useEffect(() => {
    if (expired) signInRef.current?.focus();
  }, [expired]);

  const logout = useCallback(() => {
    googleLogout();
    Cookies.remove('user');
    clearGoogleAccessToken();
    clearAgentSessionToken();
    clearAccSession();
    clearKioskFlag();
    resetUserCaches();
    loadedUserCookie.current = null;
    setUser(null);
  }, []);

  /** Signs the device out of a PIN session; the signed-out event below sends it to /login. */
  const switchKioskTrainer = useCallback((notice) => {
    endKioskSession(typeof notice === 'string' ? notice : undefined);
  }, []);

  /** Trades name + PIN for a kiosk session and becomes that trainer. Throws the server's refusal. */
  const kioskSignIn = useCallback(async (name, pin) => {
    const kioskUser = await startKioskSession(name, pin);
    loadedUserCookie.current = Cookies.get('user') ?? null;
    expiredRef.current = false;
    heldRouteRef.current = false;
    resetAccSessionExpiry();
    setExpired(false);
    setUser(kioskUser);
    return kioskUser;
  }, []);

  /** Sign out and say so, once. Signing back in re-arms it. */
  const signOutLoud = useCallback(() => {
    // The Google takeover asks for a Google sign-in, which a shared-iPad trainer does not have:
    // a lapsed PIN session goes back to the name grid instead.
    if (isKioskSession()) {
      endKioskSession(KIOSK_SIGNED_OUT_NOTICE);
      return;
    }
    if (expiredRef.current) return;
    expiredRef.current = true;
    const here = pathnameRef.current;
    heldRouteRef.current = !isSignInPath(here);
    setExpired(true);
    logout();
    // A Google user parked on the PIN screen should not be left looking at a name grid.
    if (here === KIOSK_SIGN_IN_PATH || here === '/kiosk') {
      navigateRef.current(GOOGLE_SIGN_IN_PATH, { replace: true });
    }
  }, [logout]);

  const loginWithRedirect = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError(null);
      try {
        const userInfo = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });

        const rawUser = JSON.stringify(userInfo.data);
        Cookies.set('user', rawUser, { expires: SESSION_DAYS });
        loadedUserCookie.current = rawUser;
        // A Google sign-in on a device that was a kiosk must not inherit the kiosk's rules.
        clearKioskFlag();
        resetUserCaches();
        // Retain the Google access token so authenticated features (e.g. Agent
        // Monitoring) can present the user's identity to backing APIs instead of
        // shipping a shared admin secret in the bundle. Memory, not a cookie: a
        // readable cookie hands the token to any script on the page for its full
        // lifetime, and nothing here needs it to survive a reload.
        const expiresInSeconds = typeof tokenResponse.expires_in === 'number' ? tokenResponse.expires_in : 3600;
        setGoogleAccessToken(tokenResponse.access_token, expiresInSeconds);
        // Trade it for a four-hour agent session immediately, and retry once if that fails.
        //
        // Still non-blocking - login must not wait on an agent cold start - but no longer
        // fire-and-forget. This app's cookie lasts four hours while the Google token behind it
        // lasts one, and the agent session is the only thing that covers the gap. Losing the
        // single attempt to a cold start left the Agent Monitoring page with no usable
        // credential for three hours while every other page still showed the user signed in,
        // which is exactly what the "session expired" complaints were.
        startAgentSession()
            .then((ok) => (ok ? ok : new Promise((r) => setTimeout(r, AGENT_SESSION_RETRY_MS)).then(startAgentSession)))
            .catch(() => undefined);
        // And for a signed ACC session, so the Lambda can take the operator's identity from a
        // token it signed instead of the `user_name` the browser sends. Non-blocking, and
        // retried once like the agent session: login is the only moment a Google token is
        // certain to be here, and once `REQUIRE_ACC_SESSION` is on, losing this exchange to a
        // cold start costs the trainer the whole shift, not one page.
        startAccSession()
            .then((ok) => (ok ? ok : new Promise((r) => setTimeout(r, AGENT_SESSION_RETRY_MS)).then(startAccSession)))
            .catch(() => undefined);
        const returningFromTakeover = expiredRef.current && heldRouteRef.current;
        expiredRef.current = false;
        heldRouteRef.current = false;
        resetAccSessionExpiry();
        setExpired(false);
        setUser(userInfo.data);
        // Returning from the takeover means the trainer never left the page they were on, so
        // sending them to the dashboard now would throw away the work the takeover protected.
        if (onLoginSuccess && !returningFromTakeover) {
          onLoginSuccess();
        }
      } catch (error) {
        console.error("Error during login:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error("Login error:", errorResponse);
      setError(errorResponse);
    },
  });

  // `navigate` changes identity per route, so this runs on every navigation.
  useEffect(() => {
    const checkAuth = () => {
      const userData = Cookies.get('user');
      if (!userData) {
        loadedUserCookie.current = null;
        setLoading(false);
        if (isSignInPath(pathname)) {
          return;
        }
        // The PIN session's cookie lapsed (twelve hours) with nothing to say so.
        if (isKioskSession()) {
          clearKioskFlag();
          resetUserCaches();
          setKioskNotice(KIOSK_SIGNED_OUT_NOTICE);
          setUser(null);
          navigate(KIOSK_SIGN_IN_PATH, { replace: true });
          return;
        }
        // The takeover is the reason to stay: it is holding this route open so the trainer comes
        // back to the page (and the half-written report) they were on.
        if (!expiredRef.current) {
          navigate(KIOSK_SIGN_IN_PATH);
        }
        return;
      }
      if (userData !== loadedUserCookie.current) {
        loadedUserCookie.current = userData;
        setUser(JSON.parse(userData));
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate, pathname]);

  // The command client cannot reach React state, so a rejected session with nothing left to
  // trade arrives as an event. Sign out for real: the `user` cookie outlives the Google token
  // behind it, so the alternative is a dashboard that looks signed in and 401s every request.
  useEffect(() => {
    window.addEventListener(ACC_SESSION_EXPIRED_EVENT, signOutLoud);
    return () => window.removeEventListener(ACC_SESSION_EXPIRED_EVENT, signOutLoud);
  }, [signOutLoud]);

  // The PIN session ended outside React (a 401, the idle timer, Switch trainer). The 401 path
  // drops the identity without going through endKioskSession, so the caches are reset here too.
  useEffect(() => {
    const onKioskSignedOut = () => {
      resetUserCaches();
      loadedUserCookie.current = null;
      setUser(null);
      navigate(KIOSK_SIGN_IN_PATH, { replace: true });
    };
    window.addEventListener(KIOSK_SIGNED_OUT_EVENT, onKioskSignedOut);
    return () => window.removeEventListener(KIOSK_SIGNED_OUT_EVENT, onKioskSignedOut);
  }, [navigate]);

  // Anyone already signed in when this ships holds a `user` cookie and no `acc_session`, and a
  // reload alone would never obtain one. Usually a no-op: the Google token it needs to trade
  // dies with the tab, so after a reload there is nothing to exchange. That is fine while the
  // ACC still accepts `user_name` - the next login issues the session.
  useEffect(() => {
    if (user) {
      ensureAccSession().catch(() => undefined);
    }
  }, [user]);

  // Auto-logout when the session cookie would have expired anyway. Loudly: a silent redirect
  // mid-shift reads as the app losing the trainer's work.
  useEffect(() => {
    let logoutTimer;

    // A PIN session runs on its own clock: the server's twelve hours and the idle timer.
    if (user && !isKioskUser(user)) {
      logoutTimer = setTimeout(signOutLoud, SESSION_MS);
    }

    return () => clearTimeout(logoutTimer); // Clear the timeout if the component unmounts or user logs out
  }, [user, signOutLoud]);

  return (
      <AuthContext.Provider value={{ user, loading, error, loginWithRedirect, logout, expired, kioskSignIn, switchKioskTrainer }}>
        {loading ? <div>Loading...</div> : children}
        {expired && (
            <div className="auth-expired" role="alertdialog" aria-modal="true" aria-labelledby="auth-expired-msg">
              <div className="auth-expired-card">
                <p className="auth-expired-msg" id="auth-expired-msg">Signed out. Sign in again to continue.</p>
                <button type="button" className="btn btn-primary" ref={signInRef} onClick={() => loginWithRedirect()}>
                  Sign in
                </button>
              </div>
            </div>
        )}
      </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  onLoginSuccess: PropTypes.func,
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth };